import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { Badge, Empty, Spinner, STATUS_TONE, PRIORITY_TONE, fmtDate, label } from '../components/ui.jsx';
import { t } from '../i18n/index.jsx';
import ProjectForm, { blankProject } from '../components/forms/ProjectForm.jsx';
import ContractForm, { blankContract } from '../components/forms/ContractForm.jsx';
import OfferForm, { blankOffer } from '../components/forms/OfferForm.jsx';
import TaskForm, { blankTask } from '../components/forms/TaskForm.jsx';
import DocumentList from '../components/DocumentList.jsx';
import ActivityFeed from '../components/ActivityFeed.jsx';
import PhaseStepper from '../components/PhaseStepper.jsx';
import { IconPlus } from '../components/icons.jsx';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';
const OFFER_TONE = { ...STATUS_TONE, negotiating: 'amber', accepted: 'green' };
const fmtMoney = (v) => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString());

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user.role === 'admin' || user.role === 'manager';
  const isAdmin = user.role === 'admin';

  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [auxWarn, setAuxWarn] = useState('');
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState('overview');

  // Modal state
  const [editProject, setEditProject] = useState(false);
  const [subModal, setSubModal] = useState(null); // new sub-project
  const [contractModal, setContractModal] = useState(null); // object or null
  const [offerModal, setOfferModal] = useState(null);
  const [taskModal, setTaskModal] = useState(null);

  async function load() {
    try { setData(await api.get(`/projects/${id}`)); }
    catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    if (!canEdit) return;
    api.get('/companies').then((r) => setCompanies(r.data)).catch((e) => {
      console.error('Failed to load companies', e);
      setAuxWarn(t('Could not load companies — the edit form may show empty dropdowns. Reload to retry.'));
    });
    api.get('/users').then((r) => setUsers(r.data)).catch((e) => {
      console.error('Failed to load users', e);
      setAuxWarn(t('Could not load users — the edit form may show empty dropdowns. Reload to retry.'));
    });
  }, [canEdit]);

  if (err) return <div className="alert error">{err}</div>;
  if (!data) return <Spinner />;
  const p = data.data;
  const projectOption = [{ id: p.id, name: p.name }];

  const isInternal = p.business_line === 'internal';
  const openTasks = data.tasks.filter((task) => task.status !== 'done').length;
  const TABS = [
    { key: 'overview', label: t('Overview') },
    // Offers/contracts are trade-only — an internal initiative has neither.
    ...(isInternal ? [] : [
      { key: 'deals', label: t('Offers & contracts'), count: (data.offers?.length || 0) + data.contracts.length },
    ]),
    { key: 'tasks', label: t('Tasks'), count: openTasks },
    { key: 'files', label: t('Files') },
    { key: 'activity', label: t('Activity') },
  ];

  return (
    <div className="page fade-in">
      <div className="breadcrumb">
        <Link to="/projects">{isInternal ? t('Initiatives') : t('Projects')}</Link>
        {p.parent_id && <> / <Link to={`/projects/${p.parent_id}`}>{p.parent_name}</Link></>}
        {' '}/ {p.name}
      </div>
      <div className="page-head">
        <div className="title-row">
          <h1 className="page-title">{p.name}</h1>
          <Badge tone={STATUS_TONE[p.status]}>{label(p.status)}</Badge>
        </div>
        {canEdit && (
          <div className="head-actions">
            <button className="btn ghost" onClick={() => setEditProject(true)}>{isInternal ? t('Edit initiative') : t('Edit project')}</button>
          </div>
        )}
      </div>

      {auxWarn && <div className="alert error" style={{ background: '#fffbeb', color: '#854d0e', borderColor: '#fde68a' }}>{auxWarn}</div>}

      <div className="sheet-tabs" style={{ marginBottom: 16 }}>
        {TABS.map((tb) => (
          <button
            key={tb.key}
            type="button"
            className={`chip ${tab === tb.key ? 'on' : ''}`}
            onClick={() => setTab(tb.key)}
          >
            {tb.label}{tb.count > 0 ? ` (${tb.count})` : ''}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
      <>
      <div className="detail-grid">
        {!isInternal && (
          <>
            <div className="kv"><span>{t('Manufacturers')}</span><b>{(p.manufacturers || []).map((m) => m.name).join(', ') || '—'}</b></div>
            <div className="kv"><span>{t('Clients')}</span><b>{(p.clients || []).map((c) => c.name).join(', ') || '—'}</b></div>
          </>
        )}
        <div className="kv"><span>{t('Country')}</span><b>{p.country}</b></div>
        <div className="kv"><span>{t('Our company')}</span><b>{p.own_company_name || '—'}</b></div>
        <div className="kv"><span>{t('Type')}</span><b>{p.category_name || '—'}</b></div>
        <div className="kv"><span>{t('Deadline')}</span><b>{fmtDate(p.deadline)}</b></div>
        {!isInternal && (
          <>
            <div className="kv"><span>{t('Exclusivity')}</span><b>{p.exclusivity_title || '—'}</b></div>
            <div className="kv"><span>{t('Gross / Net (€)')}</span><b>{fmtMoney(p.gross_value)} / {fmtMoney(p.net_encaissed)}</b></div>
            <div className="kv"><span>{t('Commission')}</span><b>{p.commission?.amount ? `€${Number(p.commission.amount).toLocaleString()} (${p.commission.effective_pct}%)` : '—'}</b></div>
          </>
        )}
      </div>
      {p.description && <p className="description">{p.description}</p>}

      {!p.parent_id && (
        <PhaseStepper projectId={p.id} canEdit={canEdit} companies={[...(p.clients || []), ...(p.manufacturers || [])]} />
      )}

      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t('Sub-projects')}</h2>
          {canEdit && (
            <button className="btn ghost sm" onClick={() => setSubModal({ ...blankProject, parent_id: p.id, country: p.country })}>
              <IconPlus width={13} height={13} /> {t('New sub-project')}
            </button>
          )}
        </div>
        {(!data.subprojects || data.subprojects.length === 0) ? <Empty>{t('No sub-projects.')}</Empty> : (
          <ul className="line-list">
            {data.subprojects.map((s) => (
              <li key={s.id}>
                <div className="ll-main"><Link className="ll-title" to={`/projects/${s.id}`}>{s.name}</Link></div>
                <div className="ll-meta">
                  <Badge tone={STATUS_TONE[s.status]}>{label(s.status)}</Badge>
                  <span className="due">{fmtDate(s.deadline)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      </>
      )}

      {tab === 'deals' && (
      <>
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t('Offers')}</h2>
          {canEdit && (
            <button className="btn ghost sm" onClick={() => setOfferModal({ ...blankOffer, project_id: p.id })}>
              <IconPlus width={13} height={13} /> {t('Add offer')}
            </button>
          )}
        </div>
        {(!data.offers || data.offers.length === 0) ? <Empty>{t('No offers.')}</Empty> : (
          <ul className="line-list">
            {data.offers.map((o) => (
              <li key={o.id} className={canEdit ? 'clickable' : ''} onClick={() => canEdit && setOfferModal(o)}>
                <div className="ll-main"><span className="ll-title">{o.title}</span><span className="muted sm">{o.client_name}</span></div>
                <div className="ll-meta">
                  <Badge tone={OFFER_TONE[o.status] || 'gray'}>{label(o.status)}</Badge>
                  {o.amount != null && <span className="due">{Number(o.amount).toLocaleString()} {o.currency}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t('Contracts')}</h2>
          {canEdit && (
            <button className="btn ghost sm" onClick={() => setContractModal({ ...blankContract, project_id: p.id })}>
              <IconPlus width={13} height={13} /> {t('Add contract')}
            </button>
          )}
        </div>
        {data.contracts.length === 0 ? <Empty>{t('No contracts.')}</Empty> : (
          <ul className="line-list">
            {data.contracts.map((c) => (
              <li key={c.id} className={canEdit ? 'clickable' : ''} onClick={() => canEdit && setContractModal(c)}>
                <div className="ll-main"><span className="ll-title">{c.title}</span><span className="muted sm">{c.type}</span></div>
                <div className="ll-meta">
                  <Badge tone={STATUS_TONE[c.status]}>{label(c.status)}</Badge>
                  <span className="due">{t('expires')} {fmtDate(c.expiry_date)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      </>
      )}

      {tab === 'tasks' && (
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t('Tasks')}</h2>
          {canEdit && (
            <button className="btn ghost sm" onClick={() => setTaskModal({ ...blankTask, project_id: p.id })}>
              <IconPlus width={13} height={13} /> {t('Add task')}
            </button>
          )}
        </div>
        {data.tasks.length === 0 ? <Empty>{t('No tasks.')}</Empty> : (
          <ul className="line-list">
            {data.tasks.map((task) => (
              <li key={task.id} className={canEdit ? 'clickable' : ''} onClick={() => canEdit && setTaskModal(task)}>
                <div className="ll-main"><span className="ll-title">{task.title}</span></div>
                <div className="ll-meta">
                  <Badge tone={PRIORITY_TONE[task.priority]}>{label(task.priority)}</Badge>
                  <Badge tone={STATUS_TONE[task.status]}>{label(task.status)}</Badge>
                  <span className="due">{fmtDate(task.due_date)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      )}

      {tab === 'files' && (
        <DocumentList ownerType="project" ownerId={p.id} kind="attachment" title={t('Files')} canEdit={canEdit} isAdmin={isAdmin} />
      )}

      {tab === 'activity' && (
        <ActivityFeed entityType="project" entityId={p.id} title={t('Activity')} />
      )}

      {editProject && (
        <ProjectForm
          initial={p}
          companies={companies}
          isAdmin={isAdmin}
          onClose={() => setEditProject(false)}
          onSaved={() => { setEditProject(false); load(); }}
          onDeleted={() => navigate('/projects')}
        />
      )}

      {subModal && (
        <ProjectForm
          initial={subModal}
          companies={companies}
          isAdmin={isAdmin}
          onClose={() => setSubModal(null)}
          onSaved={() => { setSubModal(null); load(); }}
        />
      )}

      {contractModal && (
        <ContractForm
          initial={contractModal}
          projects={projectOption}
          isAdmin={isAdmin}
          lockProject
          onClose={() => setContractModal(null)}
          onSaved={() => { setContractModal(null); load(); }}
        />
      )}

      {offerModal && (
        <OfferForm
          initial={offerModal}
          projects={projectOption}
          clients={(p.clients || [])}
          isAdmin={isAdmin}
          onClose={() => setOfferModal(null)}
          onSaved={() => { setOfferModal(null); load(); }}
          onAccepted={() => { setOfferModal(null); load(); }}
        />
      )}

      {taskModal && (
        <TaskForm
          initial={taskModal}
          projects={projectOption}
          users={users}
          lockProject
          onClose={() => setTaskModal(null)}
          onSaved={() => { setTaskModal(null); load(); }}
        />
      )}

    </div>
  );
}
